import {createHelpers} from 'vuex-map-fields';
import User from '../../api/user';

/* eslint-disable no-shadow */

// initial state
const state = {
  user: {
    dashboardGuid: 'primary',
    settings: {
      rowsPerPage: 10,
      defaultDashboardView: 'TILE',
      useFixedTileSize: false,
      fixedTileSize: null
    }
  },
  local: false,
  isAssumedAccount: false,
  isAssumedUser: false,
  saved: null,
  dirty: 0
};

// Because by default, getters and mutations in Vuex
// modules, are globally accessible and not namespaced,
// you most likely want to rename the getter and mutation
// helpers because otherwise you can't reuse them in multiple,
// non namespaced modules.
const {getUserField, mutateUserField} = createHelpers({
  getterType: 'getUserField',
  mutationType: 'mutateUserField'
});

// getters - all receive state as first argument
const getters = {
  isUserDirty: (state) => state.dirty,
  isLocalRunning: (state) => state.local,
  isAssumedAccount: (state) => state.isAssumedAccount,
  isAssumedUser: (state) => state.isAssumedUser,
  getUser: (state) => state.user,
  getGitBranch: (state) => state.user.gitBranch,
  getUserField
};

// actions
const actions = {
  async getUser({commit}) {
    const user = await User.get();
    const saved = await User.getSaved();
    if (saved) {
      if (saved.assumedAccount) commit('setAssumedAccount', true);
      if (saved.assumedUser) commit('setAssumedUser', true);
      commit('setSavedState', saved);
    }
    commit('setUser', user);
    commit('setAccount', user ? user.account : {});
  },
  async refreshUser({commit}) {
    const user = await User.refreshUser();
    const saved = await User.getSaved();
    if (saved) {
      commit('setAssumedAccount', true);
      commit('setSavedState', saved);
    }
    commit('setUser', user);
    commit('setAccount', user ? user.account : {});
  },
  async setUser({commit}, user) {
    commit('setUser', await User.set(user));
    commit('setAccount', user ? user.account : {});
  },
  async signout({commit}) {
    await User.signout();
    commit('setUser', {});
    commit('setAccount', {});
  },
  async updateUser({commit}) {
    commit('setUser', await User.update(state.user));
  },
  async userAssumeAccount({commit, state}, user) {
    const saved = {user: state.user, account: state.user.account, assumedAccount: true};
    commit('setSavedState', await User.setSaved(saved));
    commit('setAssumedAccount', true);
    commit('setUser', await User.set(user));
    commit('setAccount', user.account);
  },
  async userUnassumeAccount({commit, state}) {
    commit('setAssumedAccount', false);
    commit('setUser', await User.set(state.saved.user));
    commit('setAccount', state.saved.account);
    commit('setSavedState', await User.setSaved(null));
  },
  async userAssumeUser({commit, state}, user) {
    const saved = {user: state.user, account: state.user.account, assumedUser: true};
    commit('setSavedState', await User.setSaved(saved));
    commit('setAssumedUser', true);
    commit('setUser', await User.set(user));
    commit('setAccount', user.account);
  },
  async userUnassumeUser({commit, state}) {
    commit('setAssumedUser', false);
    commit('setUser', await User.set(state.saved.user));
    commit('setAccount', state.saved.account);
    commit('setSavedState', await User.setSaved(null));
  },
  async userSetTheme({commit}, dark) {
    commit('setUser', await User.setTheme(dark));
  }
};

// mutations
const mutations = {
  setLocal(state, local) {
    state.local = local;
  },
  clearUserDirty(state) {
    state.dirty = 0;
  },
  setSavedState(state, saved) {
    state.saved = saved;
  },
  setAssumedAccount(state, assumedAccount) {
    state.isAssumedAccount = assumedAccount;
  },
  setAssumedUser(state, assumedUser) {
    state.isAssumedUser = assumedUser;
  },
  setUser(state, user) {
    // https://github.com/maoberlehner/vuex-map-fields/issues/41
    state.user = user;
  },
  updateUserField(state, field) {
    state.dirty++;
    mutateUserField(state, field);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
